import {
	applyOffsets,
	getCreditorAccount,
	getPayableTerms,
	getPaymentAmount,
	getPaymentExecutionDate,
	getUnpayableReason,
} from "@msuite/utils/incoming-invoices";
import type {
	IncomingInvoiceListItem,
	IncomingInvoiceUnpayableReason,
	WorkspaceWithMeta,
} from "@msuite/utils/types";

/** One invoice of the export as the modal previews it: what is paid, and what is in the way. */
export interface PaymentPreview {
	id: string;
	number: string | null;
	supplier: string | null;
	/** The contact the invoice is booked to, linked from the supplier's name. */
	contactId: string | null;
	/** The IBAN the transfer goes to — the invoice's, else its contact's default account. */
	iban: string | null;
	workspace: IncomingInvoiceListItem["workspace"];
	currency: string;
	grossAmount: number | null;
	skontoAmount: number | null;
	/** The numbers of the planned outgoing invoices taken off the payment. */
	offsetNumbers: string[];
	/** What is transferred: the gross amount after Skonto, less the planned offsets. */
	transferAmount: number | null;
	reason: IncomingInvoiceUnpayableReason | null;
}

/**
 * The invoices picked, with the amount each is paid and the reason one cannot be — computed by the
 * same rules the server applies, so the modal says before the export what the export would refuse.
 * The outgoing invoices planned against a payment are taken off it here as they will be there; a
 * plan the payment is covered without refuses the invoice, since the server refuses the file over
 * it, and is mended on the invoice.
 *
 * @param invoices - The invoices picked in the table.
 * @param workspaces - The organisation's workspaces, with the account each pays from.
 * @param isSkontoApplied - Whether open Skonto is taken off.
 */
export function toPaymentPreviews(
	invoices: IncomingInvoiceListItem[],
	workspaces: WorkspaceWithMeta[],
	isSkontoApplied: boolean,
): PaymentPreview[] {
	const executionDate = getPaymentExecutionDate();
	const debtorIbans = new Map<string, string | null>();
	for (const workspace of workspaces) debtorIbans.set(workspace.id, workspace._meta?.iban ?? null);
	return invoices.map((invoice) => {
		const creditorName = invoice.contactName || invoice.issuerName;
		const payable = {
			...invoice,
			...getPayableTerms(invoice),
			creditorName,
			debtorIban: invoice.workspaceId ? (debtorIbans.get(invoice.workspaceId) ?? null) : null,
		};
		const reason = getUnpayableReason(payable);
		const payment = getPaymentAmount(invoice, isSkontoApplied, executionDate);
		const planned = reason === null ? invoice.plannedOffsets : [];
		const offset = toOffsetPreview(payment.amount, planned);
		// A plan outlives the contact it was made for; the server refuses it without one.
		const isPlanInvalid = offset.isUnreached || (planned.length > 0 && !invoice.contactId);
		return {
			id: invoice.id,
			number: invoice.number,
			supplier: creditorName,
			contactId: invoice.contactId,
			iban: getCreditorAccount(payable)?.iban ?? null,
			workspace: invoice.workspace,
			currency: invoice.currency,
			grossAmount: payment.grossAmount,
			skontoAmount: payment.skontoAmount,
			offsetNumbers: offset.offsetNumbers,
			transferAmount: offset.transferAmount,
			reason: reason ?? (isPlanInvalid ? "invalid-offset" : null),
		};
	});
}

/** The planned offsets taken off a payment by the rule the server applies (`applyOffsets`). */
function toOffsetPreview(
	amount: number | null,
	planned: IncomingInvoiceListItem["plannedOffsets"],
) {
	if (amount === null || !planned.length) {
		return { offsetNumbers: [], transferAmount: amount, isUnreached: false };
	}
	const result = applyOffsets(amount, planned);
	return {
		offsetNumbers: result.applied.map((offset) => offset.number),
		transferAmount: result.remaining,
		isUnreached: result.unused.length > 0,
	};
}
